import { useEffect } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { LogIn, ShieldCheck, Leaf, AlertCircle } from "lucide-react";
import { IvpLogo } from "@/components/IvpLogo";
import { loginRequest, ALLOWED_DOMAINS } from "@/authConfig";

const Shell = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="sso-gate">
      <div className="absolute" />
      <div className="mb-6 flex justify-center"><IvpLogo /></div>
      {children}
    </div>
  </div>
);

export const SsoGate = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isAuth = useIsAuthenticated();

  useEffect(() => {
    if (accounts[0]) instance.setActiveAccount(accounts[0]);
  }, [accounts, instance]);

  const account = accounts[0];
  const email = (account?.username || "").toLowerCase();
  const domainOk = ALLOWED_DOMAINS.some((d) => email.endsWith("@" + d));

  const signIn = async () => {
    try {
      const result = await instance.loginPopup(loginRequest);
      const account = result?.account || instance.getAllAccounts()[0];
      if (account) instance.setActiveAccount(account);
    } catch {
      /* user closed popup / cancelled */
    }
  };

  if (!isAuth) {
    return (
      <Shell>
        <div className="mb-2 flex items-center justify-center gap-2 text-[#16a34a]">
          <Leaf className="h-5 w-5" />
          <span className="text-sm font-semibold">IVP Fleet Management System</span>
        </div>
        <h1 className="text-center font-heading text-xl font-bold text-[#0F172A]">Sign in to continue</h1>
        <p className="mt-2 text-center text-sm text-[#64748B]">
          Use your IVP Microsoft account (@myivp.com.my or @ivpaero.com) to view and manage vehicle bookings.
        </p>
        <button
          onClick={signIn}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-[#5C2D91] px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] active:scale-[0.98]"
          data-testid="sso-signin-button"
        >
          <LogIn className="h-4 w-4" /> Sign in with Microsoft
        </button>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-[#94a3b8]">
          <ShieldCheck className="h-3.5 w-3.5" /> Secured by Microsoft Entra ID
        </p>
      </Shell>
    );
  }

  if (!domainOk) {
    return (
      <Shell>
        <div className="mb-3 flex justify-center text-red-500"><AlertCircle className="h-8 w-8" /></div>
        <h1 className="text-center font-heading text-lg font-bold text-[#0F172A]">Account not permitted</h1>
        <p className="mt-2 text-center text-sm text-[#64748B]" data-testid="sso-not-allowed">
          <span className="font-semibold text-[#0F172A]">{email || "This account"}</span> is not an authorised IVP account. Please sign in with an @myivp.com.my or @ivpaero.com account.
        </p>
        <button
          onClick={() => instance.logoutPopup({ account })}
          className="mt-6 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm font-semibold text-[#0F172A] transition-all hover:bg-slate-50"
          data-testid="sso-signout-button"
        >
          Sign out & try another account
        </button>
      </Shell>
    );
  }

  return children;
};
